import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { EngagementAssignment } from 'src/app/model/Engagement/EngagementAssignments';
import { OpportunityAndServicesForContinuanceEngagementResponseModel, OpportunityServiceByEngagementIdModel } from 'src/app/model/Client/OneViewClientDetail';
import { SearchClientOpportunityByRequestModel } from 'src/app/model/Client/CreateClientViewModel';
import { ClientService } from 'src/app/services/clientservice';

export interface AssignmentOpportunitySelection {
  assignmentId: number;
  assignmentName: string;
  solutionCode: string;
  isPrimaryAssignment: boolean;
  selectedOpportunityId?: string;
  selectedOpportunityServiceId?: string;
  isSelected: boolean;
  isCheckboxDisabled: boolean;
  availableOpportunities: OpportunityAndServicesForContinuanceEngagementResponseModel[];
  availableServices: OpportunityServiceByEngagementIdModel[];
}
@Component({
  selector: 'app-continuance-assignment-opportunity-service-popup',
  templateUrl: './continuance-assignment-opportunity-service-popup.component.html',
  styleUrls: ['./continuance-assignment-opportunity-service-popup.component.css']
})
export class ContinuanceAssignmentOpportunityServicePopupComponent implements OnInit {

  @Input() isOpen: boolean = false;
  @Output() closeModal = new EventEmitter();
  @Output() confirmSelection = new EventEmitter<AssignmentOpportunitySelection[]>();

  @Input() selectedReacceptanceClient: SearchClientOpportunityByRequestModel = new SearchClientOpportunityByRequestModel();
  @Input() assignments: EngagementAssignment[] = [];
  @Input() accountId: string = "";
  @Input() primarySolutionCode: string = "";
  @Input() opportunityList: OpportunityAndServicesForContinuanceEngagementResponseModel[] = [];
  @Input() secondarySolutionCodes: string[] = [];
  @Input() interchangeableSolutionCodes: string[] = [];

  assignmentSelections: AssignmentOpportunitySelection[] = [];
  validationMessage: string = "";

  constructor(private clientService: ClientService) { }

  ngOnInit(): void {
    this.initializeAssignmentSelections();
  }

  ngOnChanges(): void {
    if (this.assignments && this.assignments.length > 0) {
      this.initializeAssignmentSelections();
    }
  }

  initializeAssignmentSelections(): void {
    this.assignmentSelections = this.assignments.map((assignment, index) => ({
      assignmentId: assignment.assignmentCodeId,
      assignmentName: assignment.assignmentName,
      solutionCode: assignment.solutionCode,

      isPrimaryAssignment: assignment.isPrimaryAssignment,
      //First row auto selected
      isSelected: true, 
      //First row disabled
      isCheckboxDisabled: index === 0,

      selectedOpportunityId: '',  //For placeholder text
      selectedOpportunityServiceId: '',

      availableOpportunities: this.filterOpportunitiesByAssignment(assignment),
      availableServices: []
    }));

    this.refreshGrid();
  }


  filterOpportunitiesByAssignment(assignment: EngagementAssignment, currentAssignmentId?: number): OpportunityAndServicesForContinuanceEngagementResponseModel[] {
    const usedServices = this.assignmentSelections
                          .filter(x => 
                              x.assignmentId !== currentAssignmentId &&
                              x.selectedOpportunityServiceId)
                            .map(x => x.selectedOpportunityServiceId);
        
    return this.opportunityList.filter(opp => {
      const availableServices = opp.opportunityServiceList.filter(service =>
          service.osaCount === 0 &&
          service.solutionCode === assignment.solutionCode &&
          !usedServices.includes(
            service.opportunityServiceId)
      );
      return availableServices.length > 0;
    });
  }
 
onAssignmentCheckChange(selection: AssignmentOpportunitySelection): void {
    if (!selection.isSelected) {
      selection.selectedOpportunityId = '';
      selection.selectedOpportunityServiceId = '';
      selection.availableServices = [];
    }
    this.refreshGrid();

    this.validationMessage = "";
 }


  onOpportunityChange(selection: AssignmentOpportunitySelection) {
    selection.selectedOpportunityServiceId = ''; //undefined;

    this.refreshGrid();

    this.validationMessage = "";
  }

  onServiceChange(selection: AssignmentOpportunitySelection): void {
    this.refreshGrid();
    this.validationMessage = "";
  }

  onClose() {
    this.validationMessage = "";
    this.closeModal.emit();
  }

  onConfirm(): void {
    this.validationMessage = "";

    const selectedAssignments = this.assignmentSelections.filter(sel => sel.isSelected);

    if (selectedAssignments.length === 0) {
      this.validationMessage = "Please select at least one assignment.";
      return;
    }

    // Validate that selected assignments have both opportunity and service selected
    const invalidSelections = selectedAssignments.filter(sel =>
      !sel.selectedOpportunityId || !sel.selectedOpportunityServiceId
    );

    if (invalidSelections.length > 0) {
      this.validationMessage = "Please select both opportunity and service for all selected assignments.";
      return;
    }

    this.confirmSelection.emit(selectedAssignments);
    this.onClose();
  }

  getOpportunityName(opportunityId: string): string {
    const opp = this.opportunityList.find(o => o.opportunityId === opportunityId);
    return opp ? opp.opportunityName : '';
  }

  getServiceName(serviceId: string, opportunities: OpportunityAndServicesForContinuanceEngagementResponseModel[]): string {
    for (const opp of opportunities) {
      const service = opp.opportunityServiceList?.find(s => s.opportunityServiceId === serviceId);
      if (service) {
        return service.solutionDescription || service.solutionCode;
      }
    }
    return '';
  }

  private getUsedServices(): string[] {
    return this.assignmentSelections
    .filter(x =>
      x.isSelected &&
      x.selectedOpportunityServiceId)
      .map(x => x.selectedOpportunityServiceId);    
  }

  refreshGrid(): void {
    this.assignmentSelections.forEach(currentRow => {
      const assignment =
        this.assignments.find(a => a.assignmentCodeId === currentRow.assignmentId);

      if(!assignment) {
        return;
      }

      const usedServices =
        this.assignmentSelections
        .filter(x => x.assignmentId !== currentRow.assignmentId &&
                x.selectedOpportunityServiceId)
        .map(x => String(x.selectedOpportunityServiceId));
     

      currentRow.availableOpportunities =
        this.opportunityList.filter(opp => {
            const remainingServices = opp.opportunityServiceList.filter(service =>
              service.osaCount === 0 &&
              service.solutionCode === assignment.solutionCode &&

              !usedServices.includes(String(service.opportunityServiceId))
            );

            return remainingServices.length > 0;
        });

      if(currentRow.selectedOpportunityId) {
        const selectedOpp =
          currentRow.availableOpportunities.find(opp => String(opp.opportunityId) === String(currentRow.selectedOpportunityId));

          if(selectedOpp) {
            currentRow.availableServices = selectedOpp.opportunityServiceList.filter(
              service =>
                service.solutionCode === assignment.solutionCode &&
                service.osaCount === 0 &&
              !usedServices.includes(String(service.opportunityServiceId))
            );
          }
          else {
            currentRow.selectedOpportunityId = '';
            currentRow.selectedOpportunityServiceId = '';
            currentRow.availableServices = [];
          }
      }
      else {
          currentRow.selectedOpportunityServiceId = '';
          currentRow.availableServices = [];
      }
    });
  }

}
